/**
 * useDerivedStats Hook
 *
 * Derives dashboard stats from existing cached data (guards + alerts)
 * instead of polling a separate stats endpoint.
 *
 * - guardsOnDuty, patrolsInProgress, activeAlerts: derived from cache
 * - patrolsCompletedToday, patrolsTotalToday: fetched once on mount,
 *   updated via socket events, corrected on tab refocus
 */

import { useMemo, useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchDashboardStats, DashboardStats } from '../../../../../api/dispatch';
import { GuardLocation } from '../../components/guardLocation/types';
import { DispatchAlert } from '../../components/guardLocation/AlertsBar';

/** Query key for base stats (one-time fetch for non-derivable values) */
export const BASE_STATS_QUERY_KEY = ['dispatch', 'stats', 'base'];

export interface UseDerivedStatsOptions {
  /** Transformed guard locations from useGuardLocations */
  guards: GuardLocation[];
  /** Alerts from useDispatchAlerts */
  alerts: DispatchAlert[];
}

export interface UseDerivedStatsResult {
  /** Computed dashboard stats */
  stats: DashboardStats;
  /** Callback to increment completed patrols count (call from socket event) */
  onPatrolCompleted: () => void;
}

export function useDerivedStats({ guards, alerts }: UseDerivedStatsOptions): UseDerivedStatsResult {
  // Fetch base stats once for values that can't be derived client-side
  // (patrolsCompletedToday, patrolsTotalToday)
  const { data: baseStats } = useQuery({
    queryKey: BASE_STATS_QUERY_KEY,
    queryFn: () => fetchDashboardStats(),
    staleTime: Infinity,
    refetchOnWindowFocus: true, // Correct drift when user returns to tab
  });

  // Track patrol completions from socket events (delta since last server fetch)
  const [completionDelta, setCompletionDelta] = useState(0);

  const onPatrolCompleted = useCallback(() => {
    setCompletionDelta((prev) => prev + 1);
  }, []);

  // Reset delta when baseStats refreshes (e.g. on tab refocus)
  const baseCompletedToday = baseStats?.patrolsCompletedToday ?? 0;
  const baseTotalToday = baseStats?.patrolsTotalToday ?? 0;

  const stats: DashboardStats = useMemo(() => {
    // Derive from guards cache
    const guardsOnDuty = guards.filter(
      (g) => g.status === 'active' || g.status === 'stale'
    ).length;

    const patrolsInProgress = guards.filter(
      (g) => g.currentPatrol?.patrolStatus === 'in_progress'
    ).length;

    // Derive from alerts cache
    const activeAlerts = alerts.filter((a) => !a.dismissedAt).length;

    // Non-derivable: from base fetch + socket delta
    const patrolsCompletedToday = baseCompletedToday + completionDelta;
    const patrolsTotalToday = baseTotalToday;

    const patrolsStarted = patrolsInProgress + patrolsCompletedToday;
    const coveragePercent =
      patrolsTotalToday > 0
        ? Math.round((patrolsStarted / patrolsTotalToday) * 100)
        : 0;

    return {
      guardsOnDuty,
      patrolsInProgress,
      patrolsCompletedToday,
      patrolsTotalToday,
      activeAlerts,
      coveragePercent,
    };
  }, [guards, alerts, baseCompletedToday, baseTotalToday, completionDelta]);

  return { stats, onPatrolCompleted };
}
